import React, { type ReactFragment } from 'react'
import styled from '@emotion/styled'
import type { ScratchType } from '@mobi/api-types'
import { FeatureFlags } from '@mobi/settings'
import { MatchedSilkImage } from '@core/Areas/Racing/Components/SilkImage/MatchedSilkImage'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import {
  PriceChangeIndicatorInsideStyled,
  PriceInnerStyled,
  PriceWrapperStyled,
} from '@core/Components/PriceChangeDisplay/PriceChangeDisplay.styles'
import { SilkImage } from '@core/Components/SilkImage/SilkImage'
import { SingleSilkImageStyled } from '@core/Components/SilkImage/SingleSilkImage.styles'
import { Text } from '@core/Components/Text'
import type { ImageSize } from '@core/Data/Racing'
import { useFeature, usePriceChangeAnimation } from '@core/Utils/hooks'
import { ScratchedTextStyled, TipButtonStyled, TipInformationStyled } from './TipButton.styles'

export type Props = {
  number: number
  price: string
  isScratched: boolean
  toteScratchType: ScratchType | string
  isSuspended: boolean
  meetingCode?: RaceCodesLowerCase
  imageUrl: string
  imageSize: ImageSize
  isRaceOpen: boolean
  isFixedOddsOnly?: boolean
  onClick: () => void
}

export const TipButton = ({
  number,
  price,
  isScratched,
  toteScratchType,
  isSuspended,
  meetingCode,
  imageUrl,
  imageSize,
  isRaceOpen,
  isFixedOddsOnly,

  onClick,
}: Props): JSX.Element => {
  const [state] = usePriceChangeAnimation(price)
  const disabled = !isRaceOpen || isScratched || isSuspended
  const isFieldSummaryV2Active = useFeature(FeatureFlags.FIELD_SUMMARY_V2.key)

  return (
    <TipButtonStyled
      aria-disabled={disabled}
      disabled={disabled}
      isDisabled={disabled}
      isSelected={false}
      role='button'
      aria-label={`Tip button ${number}`}
      data-testid={`tip-button-${number}`}
      data-scratched={isScratched}
      data-suspended={isSuspended}
      onClick={disabled ? undefined : onClick}
    >
      <div>
        {!isFixedOddsOnly &&
          (isFieldSummaryV2Active ? (
            <MatchedSilkImage starterNumber={number} />
          ) : (
            <SilkImage
              width={imageSize.width}
              height={imageSize.height}
              url={imageUrl}
              xOffset={number - 1}
              isDogsSilk={meetingCode === 'dogs'}
              breakpoints={[{ baseWidth: 16 }, { breakpoint: 'px400', baseWidth: 25 }]}
            />
          ))}

        {isFixedOddsOnly && (
          <SilkWrapperFixedOnlyStyled>
            <SingleSilkImageStyled
              src={imageUrl}
              height={imageSize.height}
              width={imageSize.width}
              isDogsSilk={meetingCode === 'dogs'}
            />
          </SilkWrapperFixedOnlyStyled>
        )}
      </div>

      <TipInformationStyled>
        <Text size='sm'>{number}</Text>

        <strong>
          {isScratched ? (
            <ScratchedTextStyled size='lg'>{getScratchedText(toteScratchType)}</ScratchedTextStyled>
          ) : (
            <PriceWrapperStyled
              isInline={true}
              isWidthDynamic={false}
              isSelected={false}
              isPriceChanging={isRaceOpen && !!state.wpChangeTypeState.win}
            >
              {isRaceOpen && (
                <PriceChangeIndicatorInsideStyled
                  arrowDirection={state.wpChangeTypeState.win}
                  light={false}
                  isWidthDynamic={false}
                />
              )}
              <PriceInnerStyled shouldForceColor={false} centerContent={true}>
                {price}
              </PriceInnerStyled>
            </PriceWrapperStyled>
          )}
        </strong>
      </TipInformationStyled>
    </TipButtonStyled>
  )
}

function getScratchedText(toteScratchType: ScratchType | string): ReactFragment | string {
  return toteScratchType === 'Late' ? 'LATE' : 'SCR'
}

// Styles
const SilkWrapperFixedOnlyStyled = styled.div({
  display: 'inline-flex',
  alignItems: 'center',

  '> img': {
    height: 'auto',
    width: '100%',
    minWidth: '1.2em',
    objectFit: 'contain',
  },
})
